import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useManualBooking } from '../../../context/ManualBookingContext';
import { formatCurrency } from '../../../../utils/helpers';
import ImagePreview from '../../../../shared/components/ImagePreview';
import GradientButton from '../../../../components/buttons/GradientButton';
import {
  calculateAddonTax,
  calculateServiceTax
} from '../../../../utils/tax/taxCalculator';

const ServiceOptionsStep = ({ onNext, onBack }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const { bookingData, updateBookingData } = useManualBooking();

  // Theme-aware colors
  const colors = {
    background: isDarkMode ? '#0f1419' : '#ffffff',
    surface: isDarkMode ? '#1a2332' : '#f8f9fa',
    cardBackground: isDarkMode ? '#1a2332' : '#ffffff',
    selectedBackground: isDarkMode ? '#1e3a5f' : '#e3f2fd',
    selectedBorder: isDarkMode ? '#4dabf7' : '#1976d2',
    border: isDarkMode ? '#2a3b4d' : '#e1e8ed',
    textPrimary: isDarkMode ? '#ffffff' : '#2c3e50',
    textSecondary: isDarkMode ? '#94a3b8' : '#7f8c8d',
    textAccent: isDarkMode ? '#60a5fa' : '#1976d2',
    success: isDarkMode ? '#10b981' : '#388e3c',
    successBackground: isDarkMode ? '#064e3b' : '#e8f5e8',
    warning: isDarkMode ? '#f59e0b' : '#f57c00',
    warningBackground: isDarkMode ? '#78350f' : '#f3e5f5',
    error: isDarkMode ? '#ef4444' : '#d32f2f',
    errorBackground: isDarkMode ? '#7f1d1d' : '#ffebee',
    info: isDarkMode ? '#3b82f6' : '#1976d2',
    infoBackground: isDarkMode ? '#1e40af' : '#e3f2fd',
    gradientStart: isDarkMode ? '#1e293b' : '#f5f7fa',
    gradientEnd: isDarkMode ? '#334155' : '#c3cfe2'
  };

  // Get the selected service from booking data
  const service = bookingData.selectedService;

  useEffect(() => {
    // Initialize selected addons and package from booking data if they exist
    if (bookingData.selectedAddons) {
      setSelectedAddons(bookingData.selectedAddons);
    }
    if (bookingData.selectedPackage) {
      setSelectedPackageId(bookingData.selectedPackage.id);
    }
  }, []);

  if (!service) {
    return (
      <div style={{ padding: '16px', backgroundColor: colors.errorBackground, borderRadius: '8px', border: `1px solid ${colors.error}` }}>
        <p style={{ color: colors.error, margin: '0 0 16px 0' }}>No service selected. Please go back and select a service.</p>
        <div style={{ marginTop: '16px' }}>
          <GradientButton onClick={onBack} variant="primary">
            Back to Services
          </GradientButton>
        </div>
      </div>
    );
  }

  const handleAddonToggle = (addon) => {
    setSelectedAddons(prev => {
      const isSelected = prev.some(a => a.id === addon.id);
      if (isSelected) {
        return prev.filter(a => a.id !== addon.id);
      } else {
        return [...prev, addon];
      }
    });
  };

  const handlePackageSelect = (pkg) => {
    if (selectedPackageId === pkg.id) {
      setSelectedPackageId(null);
    } else {
      setSelectedPackageId(pkg.id);
    }
  };

  const handleContinue = () => {
    const selectedPackage = service.packages?.find(pkg => pkg.id === selectedPackageId) || null;

    // Process selected addons with tax
    const selectedAddonsWithTax = selectedAddons.map(addon => 
      calculateAddonTax(addon, service.taxSettings)
    );

    // Calculate complete tax for service and addons
    const { taxAmount, totalPrice, breakdown } = calculateServiceTax(
      service, 
      selectedAddonsWithTax
    );

    // Create a clean service object with all required fields
    const selectedService = {
      // Basic service info
      id: service.id,
      name: service.name || '',
      duration: parseInt(service.duration) || 60,
      price: service.price || 0,
      description: service.description || '',
      bufferTime: parseInt(service.bufferTime) || 0,
      
      // Tax information
      taxSettings: service.taxSettings || null,
      taxRate: service.taxRate || 0,
      taxAmount: taxAmount,
      taxBreakdown: breakdown,
      
      // Include original fields to maintain data consistency
      addOns: service.addOns || [],
      packages: service.packages || [],
      imageUrl: service.imageUrl || '',
      active: service.active !== false,
      createdAt: service.createdAt || new Date().toISOString(),
      updatedAt: service.updatedAt || new Date().toISOString(),
      depositSettings: service.depositSettings || null,

      // Selected options with tax
      selectedAddons: selectedAddonsWithTax,
      selectedPackage: selectedPackage,

      // Add explicit service fields that will be used in appointment creation
      serviceId: service.id,
      serviceName: service.name || '',
      serviceDuration: parseInt(service.duration) || 60,
      servicePrice: totalPrice, // Use the total price including tax
      serviceDescription: service.description || '',
    };

    // Update booking data with both nested and flat service fields
    const bookingUpdate = {
      selectedService,
      // Add service fields at root level for easier access
      serviceId: selectedService.id,
      serviceName: selectedService.name,
      serviceDuration: selectedService.duration,
      servicePrice: totalPrice, // Use the total price including tax
      serviceDescription: selectedService.description,
      // Include tax information at root level
      taxAmount: taxAmount,
      taxRate: service.taxRate || 0,
      taxBreakdown: breakdown,
      // Include selected options
      selectedAddons: selectedAddonsWithTax,
      selectedPackage: selectedPackage,
      // Reset datetime when service changes
      selectedDateTime: null,
      date: null,
      time: null,
      selectedEmployee: null,
      employeeId: null,
      employeeIds: [],
      employeeName: null,
    };

    updateBookingData(bookingUpdate);
    onNext();
  };

  // Handle both addons and addOns property names
  const serviceAddons = service.addons || service.addOns || [];
  const hasAddons = serviceAddons.length > 0;
  const hasPackages = service.packages?.length > 0;

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* Service Summary Header */}
      <div style={{
        marginBottom: '24px',
        padding: '24px',
        backgroundColor: 'transparent',
        textAlign: 'center'
      }}>
        <h2 style={{
          margin: '0 0 12px 0',
          fontSize: '1.75rem',
          fontWeight: '600',
          color: colors.textPrimary,
          letterSpacing: '-0.02em',
          lineHeight: '1.2'
        }}>
          {service.name}
        </h2>
        {service.description && (
          <p style={{
            margin: '0 0 20px 0',
            color: colors.textSecondary,
            fontSize: '0.95rem',
            lineHeight: '1.6',
            maxWidth: '600px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            {service.description}
          </p>
        )}
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '8px 16px',
            backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)',
            color: colors.info,
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {service.duration} min{service.bufferTime ? ` (+${service.bufferTime} buffer)` : ''}
          </span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '8px 16px',
            backgroundColor: isDarkMode ? 'rgba(56, 142, 60, 0.2)' : 'rgba(56, 142, 60, 0.1)',
            color: colors.success,
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: '600'
          }}>
            {formatCurrency(service.price)}
          </span>
        </div>
      </div>

      {/* Addons Section */}
      {hasAddons && (
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            margin: '0 0 16px 0',
            fontSize: '1.25rem',
            fontWeight: '600',
            color: colors.textPrimary
          }}>
            Available Add-ons
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '16px'
          }}>
            {serviceAddons.map((addon) => {
              const isSelected = selectedAddons.some(a => a.id === addon.id);
              return (
                <div
                  key={`addon-${addon.id}`}
                  onClick={() => handleAddonToggle(addon)}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? colors.selectedBackground : colors.cardBackground,
                    border: `2px solid ${isSelected ? colors.selectedBorder : colors.border}`,
                    boxShadow: isSelected ? `0 4px 12px rgba(25, 118, 210, ${isDarkMode ? '0.2' : '0.15'})` : `0 2px 4px rgba(0,0,0,${isDarkMode ? '0.2' : '0.05'})`,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.target.style.borderColor = colors.selectedBorder;
                      e.target.style.boxShadow = `0 4px 12px rgba(25, 118, 210, ${isDarkMode ? '0.15' : '0.1'})`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.target.style.borderColor = colors.border;
                      e.target.style.boxShadow = `0 2px 4px rgba(0,0,0,${isDarkMode ? '0.15' : '0.05'})`;
                    }
                  }}
                >
                  <div style={{ flex: addon.imageUrl ? '1' : '1', minWidth: '0' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{
                        fontSize: '0.9rem',
                        marginRight: '8px',
                        color: isSelected ? colors.textAccent : colors.textSecondary,
                        fontWeight: '500'
                      }}>
                        {isSelected ? 'Selected' : 'Add'}
                      </span>
                      <h4 style={{
                        margin: '0',
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: colors.textPrimary
                      }}>
                        {addon.name}
                      </h4>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      fontSize: '0.85rem',
                      color: colors.textSecondary
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: colors.success, fontWeight: '500' }}>{formatCurrency(addon.price)}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>{addon.duration} min{addon.bufferTime ? ` (+${addon.bufferTime})` : ''}</span>
                      </div>
                    </div>
                  </div>
                  {addon.imageUrl && (
                    <div style={{ flexShrink: '0' }}>
                      <ImagePreview
                        imageUrl={addon.imageUrl}
                        alt={addon.name}
                        height={60}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Packages Section */}
      {hasPackages && (
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            margin: '0 0 16px 0',
            fontSize: '1.25rem',
            fontWeight: '600',
            color: colors.textPrimary
          }}>
            Available Packages
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '20px'
          }}>
            {service.packages.map((pkg) => {
              const isSelected = selectedPackageId === pkg.id;
              return (
                <div
                  key={`package-${pkg.id}`}
                  onClick={() => handlePackageSelect(pkg)}
                  style={{
                    padding: '20px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? colors.selectedBackground : colors.cardBackground,
                    border: `2px solid ${isSelected ? colors.selectedBorder : colors.border}`,
                    boxShadow: isSelected ? `0 4px 16px rgba(25, 118, 210, ${isDarkMode ? '0.2' : '0.15'})` : `0 2px 8px rgba(0,0,0,${isDarkMode ? '0.15' : '0.08'})`,
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.target.style.borderColor = colors.selectedBorder;
                      e.target.style.boxShadow = `0 4px 16px rgba(25, 118, 210, ${isDarkMode ? '0.15' : '0.1'})`;
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.target.style.borderColor = colors.border;
                      e.target.style.boxShadow = `0 2px 8px rgba(0,0,0,${isDarkMode ? '0.15' : '0.08'})`;
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {/* Package Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    marginBottom: '16px'
                  }}>
                    {pkg.imageUrl && (
                      <div style={{ flexShrink: '0' }}>
                        <ImagePreview
                          imageUrl={pkg.imageUrl}
                          alt={pkg.name}
                          height={80}
                        />
                      </div>
                    )}
                    <div style={{ flex: '1', minWidth: '0' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px'
                      }}>
                        <span style={{
                          fontSize: '0.9rem',
                          marginRight: '8px',
                          color: isSelected ? colors.textAccent : colors.textSecondary,
                          fontWeight: '500'
                        }}>
                          {isSelected ? 'Selected' : 'Select'}
                        </span>
                        <h4 style={{
                          margin: '0',
                          fontSize: '1.25rem',
                          fontWeight: '700',
                          color: colors.textPrimary
                        }}>
                          {pkg.name}
                        </h4>
                      </div>
                      {pkg.description && (
                        <p style={{
                          margin: '0 0 12px 0',
                          color: colors.textSecondary,
                          fontSize: '0.9rem',
                          lineHeight: '1.4'
                        }}>
                          {pkg.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Package Details */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginBottom: '16px'
                  }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      backgroundColor: colors.successBackground,
                      color: colors.success,
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      fontWeight: '500'
                    }}>
                      <span>{formatCurrency(pkg.price)}</span>
                    </span>
                    {pkg.duration && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: colors.infoBackground,
                        color: colors.info,
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: '500'
                      }}>
                        <span>{`${pkg.duration} min${pkg.bufferTime ? ` (+${pkg.bufferTime})` : ''}`}</span>
                      </span>
                    )}
                    {pkg.sessions && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: colors.warningBackground,
                        color: colors.warning,
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: '500'
                      }}>
                        <span>{`${pkg.sessions} sessions${pkg.validity ? ` • ${pkg.validity} days` : ''}`}</span>
                      </span>
                    )}
                  </div>

                  {/* Included Add-ons */}
                  <div style={{
                    backgroundColor: colors.surface,
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`
                  }}>
                    <h5 style={{
                      margin: '0 0 12px 0',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: colors.textPrimary
                    }}>
                      Package Includes:
                    </h5>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {pkg.selectedAddons?.map((addon, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span style={{
                            color: colors.success,
                            fontSize: '0.8rem',
                            fontWeight: 'bold'
                          }}>•</span>
                          <div>
                            <div style={{
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              color: colors.textPrimary
                            }}>
                              {addon.name}
                            </div>
                            <div style={{
                              display: 'flex',
                              gap: '12px',
                              fontSize: '0.75rem',
                              color: colors.textSecondary,
                              marginTop: '2px'
                            }}>
                              <span>⏱️ {addon.duration} min</span>
                              <span>💰 {formatCurrency(addon.price)}</span>
                            </div>
                          </div>
                        </div>
                      ))}

                      {(!pkg.selectedAddons || pkg.selectedAddons.length === 0) && (
                        <div style={{
                          fontStyle: 'italic',
                          color: colors.textSecondary,
                          fontSize: '0.85rem'
                        }}>
                          Base service only
                        </div>
                      )}
                    </div>

                    {/* Show savings if package has a discount */}
                    {pkg.discount > 0 && (
                      <div style={{
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: `1px dashed ${colors.border}`
                      }}>
                        <div style={{
                          color: colors.success,
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}>
                          Save {pkg.discount}% with this package
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Terms if available */}
                  {pkg.terms && (
                    <div style={{
                      marginTop: '12px',
                      fontSize: '0.75rem',
                      color: colors.textSecondary,
                      fontStyle: 'italic'
                    }}>
                      {pkg.terms}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Show message if no addons or packages */}
      {!hasAddons && !hasPackages && (
        <div style={{
          padding: '24px',
          backgroundColor: colors.infoBackground,
          borderRadius: '12px',
          textAlign: 'center',
          marginBottom: '24px',
          border: `1px solid ${colors.info}`
        }}>
          <div style={{
            fontSize: '1.1rem',
            color: colors.info,
            marginBottom: '8px'
          }}>
            No add-ons or packages available
          </div>
          <p style={{
            margin: '0',
            color: colors.textSecondary,
            fontSize: '0.9rem'
          }}>
            This service comes as-is. Click continue to proceed with booking.
          </p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: `1px solid ${colors.border}`,
        gap: '16px'
      }}>
        <GradientButton
          onClick={onBack}
          variant="primary"
        >
          Back
        </GradientButton>
        <GradientButton
          variant="success"
          onClick={handleContinue}
        >
          Continue
        </GradientButton>
      </div>
    </div>
  );
};

export default ServiceOptionsStep;
